import { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { interviewService } from '../../../services/interviewService';

const ScheduleInterviewModal = ({ candidate, onClose, onInterviewScheduled }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    interviewDate: '',
    interviewTime: '',
    duration: '60',
    interviewType: 'video',
    location: '',
    meetingLink: '',
    interviewerName: '',
    interviewerEmail: '',
    notes: ''
  });

  const interviewTypeOptions = [
    { value: 'video', label: 'Video Call' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'in-person', label: 'In-Person' }
  ];

  const durationOptions = [
    { value: '30', label: '30 minutes' },
    { value: '45', label: '45 minutes' },
    { value: '60', label: '1 hour' },
    { value: '90', label: '1.5 hours' },
    { value: '120', label: '2 hours' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData?.interviewDate) {
      setError('Please select an interview date');
      return false;
    }

    if (!formData?.interviewTime) {
      setError('Please select an interview time');
      return false;
    }

    if (!formData?.interviewerName) {
      setError('Please enter interviewer name');
      return false;
    }

    if (!formData?.interviewerEmail) {
      setError('Please enter interviewer email');
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(formData?.interviewerEmail)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (formData?.interviewType === 'video' && !formData?.meetingLink) {
      setError('Please provide a meeting link for video interviews');
      return false;
    }

    if (formData?.interviewType === 'in-person' && !formData?.location) {
      setError('Please provide a location for in-person interviews');
      return false;
    }

    // Check if interview date is in the future
    const selectedDate = new Date(formData.interviewDate);
    const today = new Date();
    today?.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      setError('Interview date must be in the future');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const interviewData = {
        applicationId: candidate?.id,
        candidateId: candidate?.applicantId || candidate?.id,
        jobId: candidate?.jobId,
        candidateEmail: candidate?.email,
        candidateName: candidate?.name,
        jobTitle: candidate?.position,
        companyName: candidate?.companyName,
        interviewDate: formData?.interviewDate,
        interviewTime: formData?.interviewTime,
        durationMinutes: parseInt(formData?.duration),
        interviewType: formData?.interviewType,
        location: formData?.location || null,
        meetingLink: formData?.meetingLink || null,
        interviewerName: formData?.interviewerName,
        interviewerEmail: formData?.interviewerEmail,
        notes: formData?.notes || null
      };

      await interviewService?.scheduleInterview(interviewData);

      setSuccess(true);
      setTimeout(() => {
        onInterviewScheduled?.();
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error scheduling interview:', err);
      setError(err?.message || 'Failed to schedule interview. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date()?.toISOString()?.split('T')?.[0];

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground">Schedule Interview</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {candidate?.name} - {candidate?.position}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mx-6 mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <Icon name="CheckCircle2" size={20} className="text-green-600" />
            <div>
              <p className="text-sm font-medium text-green-900">Interview scheduled successfully!</p>
              <p className="text-xs text-green-700 mt-1">
                Email notification has been sent to {candidate?.email}
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-900">Error</p>
              <p className="text-xs text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Interview Date *
              </label>
              <Input
                type="date"
                value={formData?.interviewDate}
                onChange={(e) => handleChange('interviewDate', e?.target?.value)}
                min={today}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Interview Time *
              </label>
              <Input
                type="time"
                value={formData?.interviewTime}
                onChange={(e) => handleChange('interviewTime', e?.target?.value)}
                required
              />
            </div>
          </div>

          {/* Duration and Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Duration
              </label>
              <Select
                options={durationOptions}
                value={formData?.duration}
                onChange={(value) => handleChange('duration', value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Interview Type *
              </label>
              <Select
                options={interviewTypeOptions}
                value={formData?.interviewType}
                onChange={(value) => handleChange('interviewType', value)}
              />
            </div>
          </div>

          {/* Conditional Location/Meeting Link */}
          {formData?.interviewType === 'video' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Meeting Link *
              </label>
              <Input
                type="url"
                placeholder="https://meet.google.com/abc-defg-hij"
                value={formData?.meetingLink}
                onChange={(e) => handleChange('meetingLink', e?.target?.value)}
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Provide the video call link (Google Meet, Zoom, etc.)
              </p>
            </div>
          )}

          {formData?.interviewType === 'in-person' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Location *
              </label>
              <Input
                type="text"
                placeholder="Office address or meeting location"
                value={formData?.location}
                onChange={(e) => handleChange('location', e?.target?.value)}
                required
              />
            </div>
          )}

          {/* Interviewer Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Interviewer Name *
              </label>
              <Input
                type="text"
                placeholder="John Doe"
                value={formData?.interviewerName}
                onChange={(e) => handleChange('interviewerName', e?.target?.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Interviewer Email *
              </label>
              <Input
                type="email"
                placeholder="interviewer@company.com"
                value={formData?.interviewerEmail}
                onChange={(e) => handleChange('interviewerEmail', e?.target?.value)}
                required
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Additional Notes
            </label>
            <textarea
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground bg-background resize-none"
              rows={4}
              placeholder="Any additional information for the candidate..."
              value={formData?.notes}
              onChange={(e) => handleChange('notes', e?.target?.value)}
            />
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium">Email notification will be sent</p>
              <p className="text-blue-700 mt-1">
                The candidate will receive an email with all interview details and a calendar invitation.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading || success}
              iconName={loading ? undefined : 'Calendar'}
              iconPosition="left"
              className="flex-1"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Scheduling...
                </div>
              ) : success ? (
                'Scheduled!'
              ) : (
                'Schedule Interview'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleInterviewModal;